import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import EditGameModal from "@/components/EditGameModal"; // Import the new modal component
import { useUser } from "../../context/UserContext"; // Import useUser hook

interface Game {
  id: number;
  name: string;
  description: string;
  image_url: string | null;
  versions_count: number;
  files_count: number;
  submitted_translations_count: number;
  pending_translations_count: number;
}

const Index = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingGame, setEditingGame] = useState<Game | null>(null);
  const { user, loading: userLoading } = useUser(); // Get user and loading state from context

  const fetchGames = useCallback(async () => {
    try {
      const response = await fetch('/api/games');
      if (!response.ok) {
        throw new Error('Hiba a játékok betöltése közben.');
      }
      const data: Game[] = await response.json();
      setGames(data);
    } catch (error) {
      console.error("Hiba a játékok lekérésekor:", error);
    }
  }, []);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  const handleEditClick = (game: Game) => {
    setEditingGame(game);
    setEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setEditModalOpen(false);
    setEditingGame(null);
  };

  const handleGameSaved = () => {
    fetchGames(); // Refresh the list of games
    handleCloseModal();
  };

  const handleDelete = async (gameId: number) => {
    if (window.confirm('Biztosan törölni szeretnéd ezt a játékot?')) {
      try {
        const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
        const response = await fetch(`/api/games/${gameId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken || '',
          },
        });

        if (!response.ok) {
          throw new Error('A játék törlése sikertelen.');
        }

        fetchGames(); // Refresh games list
      } catch (error) {
        console.error("Hiba a játék törlésekor:", error);
      }
    }
  };

  return (
    <div className="bg-background text-foreground p-4">
      <h1 className="text-3xl font-bold mb-6">Játékok</h1>
      {!userLoading && user?.admin && (
        <div className="mb-6">
          <Link to="/games/new">
            <Button variant="default">Új Játék Létrehozása</Button>
          </Link>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {games.map((game) => (
          <Card key={game.id} className="flex flex-col">
            <CardHeader className="flex-grow">
              {game.image_url && (
                <Link to={`/games/${game.id}`}>
                  <img
                    src={game.image_url}
                    alt={game.name}
                    className="rounded-t-lg mb-4 aspect-video object-cover"
                  />
                </Link>
              )}
              <CardTitle>
                <Link to={`/games/${game.id}`} className="hover:underline">
                  {game.name}
                </Link>
              </CardTitle>
              <CardDescription>
                {game.description}
                <div className="mt-4 text-sm text-muted-foreground">
                  <p>Verziók: {game.versions_count}</p>
                  <p>Fájlok: {game.files_count}</p>
                  <p>Fordításra váró sorok: {game.pending_translations_count}</p>
                  <p>Beküldött fordítások: {game.submitted_translations_count}</p>
                </div>
              </CardDescription>
            </CardHeader>
            <CardFooter className="flex justify-between items-center">
              <Link to={`/games/${game.id}`} className="flex-grow mr-2">
                <Button className="w-full">Részletek</Button>
              </Link>
              {!userLoading && user?.admin && (
                <>
                  <Button variant="outline" onClick={() => handleEditClick(game)}>
                    Szerkesztés
                  </Button>
                  <Button variant="destructive" onClick={() => handleDelete(game.id)} className="ml-2">
                    Törlés
                  </Button>
                </>
              )}
            </CardFooter>
          </Card>
        ))}
      </div>

      <EditGameModal
        isOpen={editModalOpen}
        onClose={handleCloseModal}
        game={editingGame}
        onSave={handleGameSaved}
      />
    </div>
  );
};

export default Index;